import { useMemo } from 'react';

import { useFormatter } from '@eneco/i18n';

import {
  buildOxxioElectricityFixedCostsSection,
  buildOxxioElectricityGovernmentLeviesSection,
  buildOxxioElectricityRedeliverySection,
  buildOxxioElectricityVariableSection,
  buildOxxioGasFixedCostsSection,
  buildOxxioGasGovernmentLeviesSection,
  buildOxxioGasVariableSection,
} from './builders/oxxio-sections';
import type {
  OxxioElectricityRedeliverySection,
  OxxioElectricityVariableSection,
  OxxioFixedCostsSection,
  OxxioGasVariableSection,
  OxxioGovernmentLeviesSection,
  OxxioSectionsBuilderContext,
  OxxioTotalsSection,
} from './builders/oxxio-types';
import type { FieldsUnionOxxio } from './price-details-overview-oxxio.types';
import { FlowConsumerContextEnergy } from '../../../context';
import {
  getRedeliveryTariffPopoverDescription,
  getTotalUsageFromTaxBrackets,
} from '../../../eneco/components/PriceDetailsOverview/price-details-overview.helpers';
import { checkOfferIncludesDynamicPricingProduct, energyTypeIncludes, getCashbackTexts } from '../../../shared/helpers';
import { checkOnDiscountType, constructTextualDataKey } from '../../../shared/helpers/sitecore';
import { useProductItemContent } from '../../../shared/hooks';
import { EnergyType, type Offer } from '../../../shared/types';
import { OfferType } from '../../../shared/types/enums';

export type { FieldsUnionOxxio };

export interface UsePriceDetailsOverviewOxxioParams {
  flowContext: FlowConsumerContextEnergy;
  textualData?: FieldsUnionOxxio;
  offer?: Offer;
}

export interface PriceDetailsOverviewOxxioViewModel {
  flowContext: FlowConsumerContextEnergy;
  offer?: Offer;
  textualData?: FieldsUnionOxxio;
  usages?: Exclude<Offer['usages'], undefined>;
  shouldShowNotificationsPanel: boolean;
  shouldShowDynamicPricingNotification: boolean;
  shouldShowVariablePricingNotification: boolean;
  electricityVariableSection: OxxioElectricityVariableSection | null;
  electricityRedeliverySection: OxxioElectricityRedeliverySection | null;
  gasVariableSection: OxxioGasVariableSection | null;
  electricityGovernmentLeviesSection: OxxioGovernmentLeviesSection;
  gasGovernmentLeviesSection: OxxioGovernmentLeviesSection | null;
  electricityFixedCostsSection: OxxioFixedCostsSection;
  gasFixedCostsSection: OxxioFixedCostsSection | null;
  subtotalValue: number;
  subtotalLabel?: string;
  totalsSection: OxxioTotalsSection;
  productDetailsTariffSplitExplanation?: string;
  hasFutureRate: boolean;
}

export const usePriceDetailsOverviewOxxio = ({
  flowContext,
  offer,
  textualData,
}: UsePriceDetailsOverviewOxxioParams): PriceDetailsOverviewOxxioViewModel => {
  const {
    energyType = EnergyType.Electricity,
    hasDoubleMeter,
    hasSolarPanels = false,
    solarPanelsOutput,
  } = flowContext;

  const { costDetails, estimatedUsages, usages, discountType, costsYearly } = offer || {};
  const { currency, unit, number } = useFormatter();

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(offer?.contractSpan, energyType),
    offer?.discountType,
  );

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);
  const productDetails = getProductDetails(textualDataKey);

  const { electricityNormal, electricityLow, gas } = usages || {};
  const { usage, futureUsage, nettedUsage, low, high } = estimatedUsages ?? {};
  const electricityNormalValue = electricityNormal ?? 0;
  const electricityLowValue = electricityLow ?? 0;
  const gasValue = gas ?? 0;
  const usageValue = usage ?? 0;
  const futureUsageValue = futureUsage ?? 0;
  const nettedUsageValue = nettedUsage ?? 0;
  const electricityRedeliveryRemainder = costDetails?.electricityTotal?.electricityRedeliveryRefund?.netUsage ?? 0;

  const hasElectricityTaxBrackets =
    costDetails?.electricityEnergyTaxDetails && Object.keys(costDetails.electricityEnergyTaxDetails).length > 0;
  const electricityUsageTotal = hasElectricityTaxBrackets
    ? getTotalUsageFromTaxBrackets(costDetails.electricityEnergyTaxDetails)
    : electricityNormalValue + electricityLowValue;

  const hasGasTaxBrackets = costDetails?.gasEnergyTaxDetails && Object.keys(costDetails.gasEnergyTaxDetails).length > 0;
  const gasUsageTotal = hasGasTaxBrackets ? getTotalUsageFromTaxBrackets(costDetails.gasEnergyTaxDetails) : gasValue;

  const costsType = 'vatIncluded' as const;
  const hasElectricity = energyTypeIncludes(energyType, EnergyType.Electricity);
  const hasGas = energyTypeIncludes(energyType, EnergyType.Gas);

  const isDynamicOffer = !discountType?.includes(OfferType.Variable) && checkOfferIncludesDynamicPricingProduct(offer);
  const isDynamicOrVariableOffer =
    discountType?.includes(OfferType.Variable) || checkOfferIncludesDynamicPricingProduct(offer);

  const shouldShowDynamicPricingNotification =
    isDynamicOffer && !!textualData?.costDetails?.dynamicPricingNotification?.value?.variant;

  const shouldShowVariablePricingNotification = Boolean(
    discountType?.includes(OfferType.Variable) && textualData?.productData?.variableProductNotification?.value?.content,
  );

  const shouldShowRedeliveryNotification =
    hasSolarPanels &&
    (electricityRedeliveryRemainder && electricityRedeliveryRemainder > 0
      ? !!textualData?.costDetails?.redeliveryMoreNotification.value.content
      : !!textualData?.costDetails?.redeliveryLessNotification.value.content);

  const shouldShowNotificationsPanel =
    shouldShowDynamicPricingNotification || shouldShowRedeliveryNotification || shouldShowVariablePricingNotification;

  let monthlyCostsTotal = 0;
  if (hasElectricity) {
    monthlyCostsTotal += costDetails?.electricityTotal?.monthlyCosts?.[costsType] ?? 0;
  }
  if (hasGas) {
    monthlyCostsTotal += costDetails?.gasTotal?.monthlyCosts?.[costsType] ?? 0;
  }

  let yearlyCostsTotal = 0;
  if (hasElectricity) {
    yearlyCostsTotal += costDetails?.electricityTotal?.yearlyCosts?.[costsType] ?? 0;
  }
  if (hasGas) {
    yearlyCostsTotal += costDetails?.gasTotal?.yearlyCosts?.[costsType] ?? 0;
  }

  const cashbackTexts = getCashbackTexts(costDetails?.cashback?.type, textualData?.promotionTexts);
  const futureRate = offer?.productFutureRate;

  const redeliveryPopoverContent = useMemo(
    () =>
      getRedeliveryTariffPopoverDescription({
        costDetails,
        solarPanelsOutput,
        popoverDescription: isDynamicOffer
          ? textualData?.costDetails?.redeliveryDynamicTariffPopoverDescription?.value
          : textualData?.costDetails?.redeliveryTariffPopoverDescription?.value,
        usage: usageValue,
        nettedUsage: nettedUsageValue,
        futureUsage: futureUsageValue,
        formatElectricity: unit.electricity,
      }),
    [
      costDetails,
      solarPanelsOutput,
      isDynamicOffer,
      textualData?.costDetails?.redeliveryDynamicTariffPopoverDescription?.value,
      textualData?.costDetails?.redeliveryTariffPopoverDescription?.value,
      usageValue,
      nettedUsageValue,
      futureUsageValue,
      unit.electricity,
    ],
  );

  const sectionsContext = useMemo<OxxioSectionsBuilderContext>(
    () => ({
      costDetails,
      costsType,
      textualData,
      offer,
      hasDoubleMeter,
      hasSolarPanels,
      solarPanelsOutput,
      electricityNormal: electricityNormalValue,
      electricityLow: electricityLowValue,
      gas: gasValue,
      estimatedElectricityHigh: high,
      estimatedElectricityLow: low,
      electricityUsageTotal,
      gasUsageTotal,
      electricityRedeliveryRemainder,
      isDynamicOffer,
      isDynamicOrVariableOffer,
      formatter: { currency, number, unit },
    }),
    [
      costDetails,
      textualData,
      offer,
      hasDoubleMeter,
      hasSolarPanels,
      solarPanelsOutput,
      electricityNormalValue,
      electricityLowValue,
      gasValue,
      high,
      low,
      electricityUsageTotal,
      gasUsageTotal,
      electricityRedeliveryRemainder,
      isDynamicOffer,
      isDynamicOrVariableOffer,
      currency,
      number,
      unit,
    ],
  );

  const electricityVariableSection = useMemo(
    () => (hasElectricity ? buildOxxioElectricityVariableSection(sectionsContext) : null),
    [hasElectricity, sectionsContext],
  );

  const electricityRedeliverySection = useMemo(
    () => (hasElectricity ? buildOxxioElectricityRedeliverySection(sectionsContext, redeliveryPopoverContent) : null),
    [hasElectricity, sectionsContext, redeliveryPopoverContent],
  );

  const gasVariableSection = useMemo(
    () => (hasGas ? buildOxxioGasVariableSection(sectionsContext) : null),
    [hasGas, sectionsContext],
  );

  const electricityGovernmentLeviesSection = useMemo(
    () => buildOxxioElectricityGovernmentLeviesSection(sectionsContext),
    [sectionsContext],
  );

  const gasGovernmentLeviesSection = useMemo(
    () => (hasGas ? buildOxxioGasGovernmentLeviesSection(sectionsContext) : null),
    [hasGas, sectionsContext],
  );

  const electricityFixedCostsSection = useMemo(
    () => buildOxxioElectricityFixedCostsSection(sectionsContext),
    [sectionsContext],
  );

  const gasFixedCostsSection = useMemo(
    () => (hasGas ? buildOxxioGasFixedCostsSection(sectionsContext) : null),
    [hasGas, sectionsContext],
  );

  return {
    flowContext,
    offer,
    textualData,
    usages,
    shouldShowNotificationsPanel,
    shouldShowDynamicPricingNotification,
    shouldShowVariablePricingNotification,
    electricityVariableSection,
    electricityRedeliverySection,
    gasVariableSection,
    electricityGovernmentLeviesSection,
    gasGovernmentLeviesSection,
    electricityFixedCostsSection,
    gasFixedCostsSection,
    subtotalValue: costsYearly?.[costsType] ?? 0,
    subtotalLabel: textualData?.costDetails?.subTotalCostsLabel?.value,
    totalsSection: {
      cashbackTexts: cashbackTexts ?? undefined,
      cashbackValue: costDetails?.cashback?.value,
      yearlyTotal: yearlyCostsTotal - (costDetails?.cashback?.value ?? 0),
      monthlyTotal: monthlyCostsTotal,
      yearlyLabel: textualData?.costDetails?.yearlyTermLabel?.value,
      monthlyLabel: textualData?.costDetails?.monthlyTermLabel?.value,
    },
    productDetailsTariffSplitExplanation: productDetails?.tariffSplitExplanation,
    hasFutureRate: !!futureRate,
  };
};
